import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Lock, Calendar, ArrowRight, Layers, ChevronLeft, ChevronRight, CalendarDays, LibraryBig, BookOpenCheck, BookOpen, Mail, BookText, Users } from "lucide-react";

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function MiniCalendar({ year, month, availableDates, selectedDate, onPick, onPrev, onNext }) {
  const firstDay = new Date(year, month, 1);
  const dim = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay.getDay();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  const monthName = firstDay.toLocaleString(undefined, { month: "long", year: "numeric" });
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onPrev} className="p-1" data-testid="dash-cal-prev" style={{ color: "var(--ink-2)" }}><ChevronLeft size={18} /></button>
        <div className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>{monthName}</div>
        <button onClick={onNext} className="p-1" data-testid="dash-cal-next" style={{ color: "var(--ink-2)" }}><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[0.65rem] uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>
        {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = `${year}-${pad(month + 1)}-${pad(d)}`;
          const has = availableDates.has(iso);
          const isSelected = iso === selectedDate;
          return (
            <button
              key={i}
              onClick={() => has && onPick(iso)}
              disabled={!has}
              data-testid={`dash-cal-day-${iso}`}
              className="aspect-square text-sm rounded-sm transition-colors"
              style={{
                background: isSelected ? "var(--ember)" : "transparent",
                color: isSelected ? "var(--bg-base)" : has ? "var(--ink)" : "var(--muted)",
                fontWeight: isSelected ? 700 : has ? 500 : 400,
                cursor: has ? "pointer" : "default",
                opacity: has ? 1 : 0.35,
                border: has && !isSelected ? "1px solid var(--border-subtle)" : "1px solid transparent",
              }}
            >
              {d}
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-[0.65rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
        Highlighted = published days
      </p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [todayReading, setTodayReading] = useState(null);
  const [readingDates, setReadingDates] = useState([]);
  const [readingsByDate, setReadingsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const today = todayIso();
  const [calYear, setCalYear] = useState(parseInt(today.slice(0, 4)));
  const [calMonth, setCalMonth] = useState(parseInt(today.slice(5, 7)) - 1);
  const readingDateSet = useMemo(() => new Set(readingDates), [readingDates]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/members/series"),
      api.get("/members/reading-plan"),
    ])
      .then(([se, rd]) => {
        setSeries(se.data);
        const list = rd.data || [];
        setTodayReading(list.find((r) => r.date === today) || list[0] || null);
        setReadingDates(list.map((r) => r.date));
        setReadingsByDate(Object.fromEntries(list.map((r) => [r.date, r])));
      })
      .catch((e) => setError(e?.response?.status === 403 ? "membership" : "unknown"))
      .finally(() => setLoading(false));
  }, [user, today]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <h1 className="font-serif-display text-3xl mt-6" style={{ color: "var(--ink)" }}>Sign in to continue</h1>
        <Link to="/login" data-testid="dashboard-login-redirect" className="btn-primary mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  if (error === "membership" || (!loading && !user.membership_active && !user.is_admin)) {
    return (
      <div data-testid="dashboard-paywall" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <div className="overline mt-6">Members only</div>
        <h1 className="font-serif-display text-4xl mt-3" style={{ color: "var(--ink)" }}>This library is for members.</h1>
        <p className="mt-5 leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Join The Disciple Life for $4.99/month to unlock daily devotionals, weekly Bible studies, the monthly newsletter, and early-access chapters.
        </p>
        <Link to="/membership" data-testid="dashboard-subscribe-link" className="btn-primary mt-8 inline-flex">Become a member</Link>
      </div>
    );
  }

  // Show top 2 featured series on dashboard; the rest live in the Library
  const featuredSeries = series.slice(0, 2);

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
      {/* Hero status card */}
      <div className="card-surface p-8 lg:p-10 mb-12 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <div className="overline mb-2">Welcome back</div>
          <h1 className="font-serif-display text-3xl lg:text-4xl leading-tight" style={{ color: "var(--ink)" }}>
            Grace and peace, {user.name.split(" ")[0]}.
          </h1>
          <p className="mt-3" style={{ color: "var(--ink-2)" }}>
            Today's reading is below. Jump straight to any section of the library:
          </p>
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-quick-links">
            <Link
              to="/read-with-me"
              data-testid="quick-link-read-with-me"
              className="group flex items-center justify-between gap-3 p-4 border transition-colors hover:border-[var(--ember)]"
              style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
            >
              <span className="flex items-center gap-3">
                <CalendarDays size={20} strokeWidth={1.75} style={{ color: "var(--ember)" }} />
                <span className="font-serif-display text-base" style={{ color: "var(--ink)" }}>Read With Me</span>
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: "var(--ember)" }} />
            </Link>
            <Link
              to="/dashboard/library?type=study"
              data-testid="quick-link-studies"
              className="group flex items-center justify-between gap-3 p-4 border transition-colors hover:border-[var(--olive)]"
              style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
            >
              <span className="flex items-center gap-3">
                <BookOpen size={20} strokeWidth={1.75} style={{ color: "var(--olive)" }} />
                <span className="font-serif-display text-base" style={{ color: "var(--ink)" }}>Bible studies</span>
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: "var(--olive)" }} />
            </Link>
            <Link
              to="/dashboard/library?type=series"
              data-testid="quick-link-series"
              className="group flex items-center justify-between gap-3 p-4 border transition-colors hover:border-[var(--terracotta)]"
              style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
            >
              <span className="flex items-center gap-3">
                <Layers size={20} strokeWidth={1.75} style={{ color: "var(--terracotta)" }} />
                <span className="font-serif-display text-base" style={{ color: "var(--ink)" }}>Study series</span>
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: "var(--terracotta)" }} />
            </Link>
            <Link
              to="/dashboard/library?type=newsletter"
              data-testid="quick-link-newsletters"
              className="group flex items-center justify-between gap-3 p-4 border transition-colors hover:border-[var(--gold)]"
              style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
            >
              <span className="flex items-center gap-3">
                <Mail size={20} strokeWidth={1.75} style={{ color: "var(--gold)" }} />
                <span className="font-serif-display text-base" style={{ color: "var(--ink)" }}>Newsletters</span>
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: "var(--gold)" }} />
            </Link>
            <Link
              to="/dashboard/commentary"
              data-testid="quick-link-commentary"
              className="group flex items-center justify-between gap-3 p-4 border transition-colors hover:border-[var(--ink)]"
              style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
            >
              <span className="flex items-center gap-3">
                <BookText size={20} strokeWidth={1.75} style={{ color: "var(--ink)" }} />
                <span className="font-serif-display text-base" style={{ color: "var(--ink)" }}>Commentary</span>
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: "var(--ink)" }} />
            </Link>
            <Link
              to="/my-family"
              data-testid="quick-link-my-family"
              className="group flex items-center justify-between gap-3 p-4 border transition-colors hover:border-[var(--ember)]"
              style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}
            >
              <span className="flex items-center gap-3">
                <Users size={20} strokeWidth={1.75} style={{ color: "var(--ember)" }} />
                <span className="font-serif-display text-base" style={{ color: "var(--ink)" }}>My family</span>
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: "var(--ember)" }} />
            </Link>
          </div>
          <Link
            to="/dashboard/library"
            data-testid="dashboard-browse-library-cta"
            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]"
            style={{ color: "var(--ember)" }}
          >
            <LibraryBig size={14} /> Or browse the full library →
          </Link>
        </div>
        <div className="text-sm border-l pl-6" style={{ borderColor: "var(--border-subtle)", color: "var(--ink-2)" }}>
          <div className="overline mb-2">Membership</div>
          {user.is_admin ? (
            <p className="font-medium" style={{ color: "var(--olive)" }}>Admin · lifetime</p>
          ) : (
            <p>
              Active through<br />
              <span className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>
                {new Date(user.membership_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
              </span>
            </p>
          )}
          {/* Legacy subscribers signed up before recurring Stripe subs existed.
              Prompt them to move onto a real auto-renewing plan. */}
          {!user.is_admin && user.stripe_customer_id && !user.stripe_subscription_id && (
            <div
              className="mt-4 p-3 rounded-md text-xs"
              style={{ background: "var(--surface-hover)", border: "1px solid var(--gold)", color: "var(--ink-2)" }}
              data-testid="dashboard-legacy-subscriber-banner"
            >
              <strong style={{ color: "var(--gold)" }}>Auto-renewal isn't set up.</strong>
              <p className="mt-1">You paid once but your card isn't on file for renewals. Move onto the recurring plan to stay covered.</p>
              <Link to="/membership" data-testid="dashboard-migrate-subscription" className="mt-2 inline-block underline" style={{ color: "var(--terracotta)" }}>
                Set up auto-renewal
              </Link>
            </div>
          )}
          <Link to="/membership" data-testid="dashboard-extend-link" className="mt-4 inline-block text-xs underline" style={{ color: "var(--terracotta)" }}>
            Extend membership
          </Link>
          {!user.is_admin && (
            <button
              data-testid="dashboard-manage-billing"
              onClick={async () => {
                try {
                  const { data } = await api.post("/subscriptions/portal", { origin_url: window.location.origin });
                  window.location.href = data.url;
                } catch (e) {
                  alert("Could not open billing portal. Try subscribing first.");
                }
              }}
              className="mt-2 block text-xs underline"
              style={{ color: "var(--gold)" }}
            >
              Manage billing
            </button>
          )}
        </div>
      </div>

      {/* Today's Read With Me */}
      <section className="mb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline mb-2">Today</div>
            <h2 className="font-serif-display text-3xl lg:text-5xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
              Read with <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>me</span>
            </h2>
          </div>
          <CalendarDays size={28} strokeWidth={1.5} style={{ color: "var(--ember)" }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Today's reading (left) */}
          <div className="lg:col-span-8">
            {todayReading ? (
              <article className="card-surface p-8 lg:p-10">
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] mb-4 flex-wrap">
                  <span style={{ color: "var(--gold)" }}>
                    {new Date(todayReading.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                  </span>
                  {todayReading.date === today && (
                    <span className="px-2 py-0.5" style={{ background: "var(--ember)", color: "var(--bg-base)" }}>Today</span>
                  )}
                  <span style={{ color: "var(--muted)" }}>Day {todayReading.day_number}</span>
                </div>
                {todayReading.title && (
                  <h3 className="font-serif-display text-3xl lg:text-4xl tracking-[-0.03em] leading-[1] mb-4" style={{ color: "var(--ink)" }}>
                    {todayReading.title}
                  </h3>
                )}
                <div className="space-y-1 mb-2">
                  <div className="overline" style={{ fontSize: "0.6rem" }}>Today's reading</div>
                  <p className="font-serif-display-italic text-xl lg:text-2xl" style={{ color: "var(--ember)" }}>
                    {todayReading.passage}
                  </p>
                  {todayReading.key_scripture && (
                    <p className="font-serif-display-italic text-base lg:text-lg" style={{ color: "var(--terracotta)" }}>
                      {todayReading.key_scripture}
                    </p>
                  )}
                </div>
                <div className="editorial-rule my-6" />

                {todayReading.reflection ? (
                  <div className="text-base lg:text-lg leading-[1.85] whitespace-pre-line" style={{ color: "var(--ink-2)" }} data-testid="dashboard-devo-body">
                    {todayReading.reflection}
                  </div>
                ) : (
                  <p className="text-sm italic" style={{ color: "var(--muted)" }}>Reflection coming after Cody finishes today's reading.</p>
                )}

                {todayReading.verse_notes && todayReading.verse_notes.length > 0 && (
                  <div className="mt-8 pt-6 border-t space-y-5" style={{ borderColor: "var(--border-subtle)" }}>
                    <div className="overline" style={{ fontSize: "0.65rem" }}>Verse notes</div>
                    {todayReading.verse_notes.map((vn, i) => (
                      <div key={i} className="border-l-2 pl-4" style={{ borderColor: "var(--terracotta)" }}>
                        <div className="font-serif-display-italic text-base" style={{ color: "var(--terracotta)" }}>{vn.ref}</div>
                        <p className="mt-2 text-sm lg:text-base leading-[1.75] whitespace-pre-line" style={{ color: "var(--ink-2)" }}>
                          {vn.note}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <Link to={`/read-with-me#day-${todayReading.day_number}`} className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ember)" }}>
                  Open in archive <ArrowRight size={12} />
                </Link>
              </article>
            ) : (
              <div className="card-surface p-10 text-center" style={{ color: "var(--muted)" }}>
                No reading day has been published yet. Check back soon.
              </div>
            )}
          </div>

          {/* Calendar (right) */}
          <aside className="lg:col-span-4">
            <MiniCalendar
              year={calYear}
              month={calMonth}
              availableDates={readingDateSet}
              selectedDate={todayReading?.date}
              onPick={(d) => {
                const r = readingsByDate[d];
                if (r) navigate(`/read-with-me#day-${r.day_number}`);
              }}
              onPrev={() => {
                const m = calMonth - 1;
                if (m < 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(m);
              }}
              onNext={() => {
                const m = calMonth + 1;
                if (m > 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(m);
              }}
            />
            <Link to="/read-with-me" data-testid="dashboard-devo-archive-link" className="btn-ghost w-full justify-center mt-4">
              Open full archive
            </Link>
          </aside>
        </div>
      </section>

      {/* Featured Study Series (only 2) */}
      {featuredSeries.length > 0 && (
        <section className="mb-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline mb-2">Continue with</div>
              <h2 className="font-serif-display text-3xl lg:text-5xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
                Featured series
              </h2>
            </div>
            <Layers size={28} strokeWidth={1.5} style={{ color: "var(--ember)" }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredSeries.map((s) => (
              <Link
                key={s.id}
                to={`/dashboard/series/${s.slug}`}
                data-testid={`series-card-${s.slug}`}
                className="card-surface p-8 lg:p-10 block group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="overline mb-3" style={{ fontSize: "0.65rem" }}>
                      {s.lesson_count} lesson{s.lesson_count === 1 ? "" : "s"}
                      {s.target_lessons > 0 && ` · of ${s.target_lessons} planned`}
                    </div>
                    <h3 className="font-serif-display text-3xl lg:text-4xl tracking-[-0.03em] leading-tight group-hover:text-[var(--ember)] transition-colors" style={{ color: "var(--ink)" }}>
                      {s.name}
                    </h3>
                    <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>
                      {s.description}
                    </p>
                  </div>
                  <ArrowRight size={20} className="shrink-0 mt-1 group-hover:translate-x-1 transition-transform" style={{ color: "var(--ember)" }} />
                </div>
                {s.target_lessons > 0 && (
                  <div className="mt-6 h-1 rounded-full overflow-hidden" style={{ background: "var(--bg-base)" }}>
                    <div className="h-full" style={{ width: `${Math.min(100, (s.lesson_count / s.target_lessons) * 100)}%`, background: "var(--ember)" }} />
                  </div>
                )}
              </Link>
            ))}
          </div>
          {series.length > featuredSeries.length && (
            <div className="mt-6 text-center">
              <Link
                to="/dashboard/library?type=series"
                data-testid="dashboard-all-series-link"
                className="text-xs uppercase tracking-[0.2em]"
                style={{ color: "var(--ember)" }}
              >
                View all {series.length} series →
              </Link>
            </div>
          )}
        </section>
      )}

      {/* Library CTA strip */}
      <section data-testid="dashboard-library-cta-strip">
        <Link
          to="/dashboard/library"
          className="card-surface p-8 lg:p-10 block group flex flex-col md:flex-row md:items-center justify-between gap-6"
        >
          <div className="flex items-start gap-5">
            <LibraryBig size={36} strokeWidth={1.5} style={{ color: "var(--ember)" }} className="shrink-0 mt-1" />
            <div>
              <div className="overline mb-2">Search & browse</div>
              <h2 className="font-serif-display text-2xl lg:text-3xl tracking-[-0.02em] leading-tight" style={{ color: "var(--ink)" }}>
                Explore the full library
              </h2>
              <p className="mt-2 text-sm lg:text-base" style={{ color: "var(--ink-2)" }}>
                Every Read With Me day, Bible study, series, and newsletter — searchable by title, scripture, or topic.
              </p>
            </div>
          </div>
          <ArrowRight size={24} className="shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: "var(--ember)" }} />
        </Link>
      </section>
    </div>
  );
}
